import {
  DEFAULT_OPENING_HOURS, READY_PLACEHOLDERS, WEEKDAYS, openingHoursProblems, parseOpeningHours, serializeOpeningHours, smsSegments, unknownPlaceholders,
  type OpeningHours, type Weekday,
} from "@szerviz/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";
import { defaultTemplate, useReadyTexts } from "./ReadyNotice";

const isOn = (v: unknown, dflt: boolean) => (v === undefined || v === null ? dflt : v === true || v === "true");

/** The opening hours, one row per weekday: open tick, from, to (phone friendly). */
export function OpeningHoursEditor({ value, onChange, disabled }: { value: OpeningHours; onChange: (h: OpeningHours) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const bad = new Set(openingHoursProblems(value));
  const set = (d: Weekday, v: OpeningHours[Weekday]) => onChange({ ...value, [d]: v });
  return (
    <div className="hours-editor" role="group" aria-label={t("setting.openingHours.label")}>
      {WEEKDAYS.map((d) => {
        const day = value[d];
        const name = t(`readyNotice.dayLong.${d}`);
        return (
          <div key={d} className={`hours-row${bad.has(d) ? " invalid" : ""}`}>
            <strong>{t(`readyNotice.day.${d}`)}</strong>
            <label className="consent-channel">
              <input
                type="checkbox"
                checked={!!day}
                disabled={disabled}
                aria-label={`${name}: ${t("readyNotice.hoursOpen")}`}
                onChange={(e) => set(d, e.target.checked ? (DEFAULT_OPENING_HOURS[1] as { from: string; to: string }) : null)}
              />
              <span>{t("readyNotice.hoursOpen")}</span>
            </label>
            {day ? (
              <span className="hours-times">
                <input type="time" value={day.from} disabled={disabled} aria-label={`${name}: ${t("readyNotice.hoursFrom")}`} onChange={(e) => set(d, { ...day, from: e.target.value })} />
                <span aria-hidden="true">–</span>
                <input type="time" value={day.to} disabled={disabled} aria-label={`${name}: ${t("readyNotice.hoursTo")}`} onChange={(e) => set(d, { ...day, to: e.target.value })} />
              </span>
            ) : (
              <span className="closed-word">{t("readyNotice.hoursClosed")}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Settings of the "car ready" notice: amount yes or no, the opening hours (given or not, shown in the notice or
 * not), the e-mail and the SMS text with their placeholders and a preview with sample data.
 */
export function ReadyNoticeSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const texts = useReadyTexts();
  const g = workshop.general;
  const emailDefault = defaultTemplate(t, "readyNotice.defaultEmail");
  const smsDefault = defaultTemplate(t, "readyNotice.defaultSms");
  const [amount, setAmount] = useState(isOn(g.readyNoticeAmount, true));
  const [hoursOn, setHoursOn] = useState(isOn(g.openingHoursOn, false));
  const [hours, setHours] = useState<OpeningHours>(() => parseOpeningHours(g.openingHours) ?? DEFAULT_OPENING_HOURS);
  const [hoursInNotice, setHoursInNotice] = useState(isOn(g.readyNoticeHours, true));
  const [email, setEmail] = useState(typeof g.readyNoticeEmail === "string" && g.readyNoticeEmail.trim() ? g.readyNoticeEmail : emailDefault);
  const [sms, setSms] = useState(typeof g.readyNoticeSms === "string" && g.readyNoticeSms.trim() ? g.readyNoticeSms : smsDefault);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const draft: Record<string, unknown> = {
    ...g, readyNoticeAmount: amount, openingHoursOn: hoursOn, openingHours: serializeOpeningHours(hours), readyNoticeHours: hoursInNotice,
    readyNoticeEmail: email, readyNoticeSms: sms,
  };
  const sample = texts({
    order: { number: "ML-2026-00042", plateNumber: "ABC123", plateCountry: "HU", vehicleLabel: "Skoda Octavia" },
    customerName: t("readyNotice.sampleName"), gross: 48460, token: null, general: draft,
  });
  const badDays = hoursOn ? openingHoursProblems(hours) : [];
  const unknown = [...new Set([...unknownPlaceholders(email), ...unknownPlaceholders(sms)])];
  const missing = [
    ...(badDays.length ? [t("readyNotice.hoursBad", { days: badDays.map((d) => t(`readyNotice.day.${d}`)).join(", ") })] : []),
    ...(!email.trim() ? [t("setting.readyNoticeEmail.label")] : []),
    ...(!sms.trim() ? [t("setting.readyNoticeSms.label")] : []),
  ];
  const smsInfo = smsSegments(sample.sms);

  const save = async () => {
    setError(null);
    setSaved(false);
    try {
      await store.saveGeneralSettings(workshop.id, {
        ...(g as Record<string, number | string | boolean | null>),
        readyNoticeAmount: amount, openingHoursOn: hoursOn, openingHours: serializeOpeningHours(hours), readyNoticeHours: hoursInNotice,
        // the program's own text stays null, so it follows the language (and later improvements)
        readyNoticeEmail: email.trim() === emailDefault.trim() ? null : email, readyNoticeSms: sms.trim() === smsDefault.trim() ? null : sms,
      });
      await reloadWorkshop();
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  };

  const tick = (key: string, value: boolean, set: (v: boolean) => void) => (
    <label className="consent-main">
      <input type="checkbox" checked={value} disabled={!canEdit} aria-label={t(`setting.${key}.label`)} onChange={(e) => set(e.target.checked)} />
      <span>
        <strong>{t(`setting.${key}.label`)}</strong>
        <br />
        <small className="muted">{t(`setting.${key}.description`)}</small>
      </span>
    </label>
  );

  return (
    <>
      <h2 id="ready-notice-settings">
        {t("readyNotice.settingsTitle")} <Help id="readyNotice.settings" />
      </h2>
      <div className="ready-settings">
        <p className="muted">{t("readyNotice.settingsIntro")}</p>
        {tick("readyNoticeAmount", amount, setAmount)}
        {tick("openingHoursOn", hoursOn, setHoursOn)}
        {hoursOn && (
          <>
            <OpeningHoursEditor value={hours} onChange={setHours} disabled={!canEdit} />
            {tick("readyNoticeHours", hoursInNotice, setHoursInNotice)}
          </>
        )}
        <label className="field wide">
          <span className="field-label">{t("setting.readyNoticeEmail.label")}</span>
          <textarea rows={10} value={email} disabled={!canEdit} aria-label={t("setting.readyNoticeEmail.label")} onChange={(e) => setEmail(e.target.value)} />
          <small>{t("setting.readyNoticeEmail.description")}</small>
        </label>
        <label className="field wide">
          <span className="field-label">{t("setting.readyNoticeSms.label")}</span>
          <textarea rows={4} value={sms} disabled={!canEdit} aria-label={t("setting.readyNoticeSms.label")} onChange={(e) => setSms(e.target.value)} />
          <small>{t("setting.readyNoticeSms.description")}</small>
        </label>
        {canEdit && (
          <div className="actions">
            <button type="button" onClick={() => { setEmail(emailDefault); setSms(smsDefault); }}>{t("readyNotice.reset")}</button>
          </div>
        )}
        <details>
          <summary>{t("readyNotice.placeholders")}</summary>
          <ul className="ph-list">
            {READY_PLACEHOLDERS.map((k) => (
              <li key={k}>
                <code>{`{{${k}}}`}</code> {t(`readyNotice.ph.${k}`)}
              </li>
            ))}
          </ul>
          <p className="muted small">{t("readyNotice.placeholdersNote")}</p>
        </details>
        {unknown.length > 0 && <p className="warn-text">{t("readyNotice.unknown", { list: unknown.map((k) => `{{${k}}}`).join(", ") })}</p>}
        <div className="ready-preview">
          <p className="ps-cap">{t("readyNotice.sample")} · {t("channels.email")}: {sample.subject}</p>
          <pre className="msg-text">{sample.email}</pre>
          <p className="ps-cap">{t("readyNotice.sample")} · {t("readyNotice.previewSms", { count: smsInfo.segments })}</p>
          <pre className="msg-text">{sample.sms}</pre>
          {sample.smsPlain && <p className="muted small">{t("readyNotice.smsPlain")}</p>}
        </div>
        {canEdit && (
          <div className="actions">
            <ErrorText error={error} />
            {saved && <span className="ok">{t("common.saved")}</span>}
            <button type="button" className="primary" disabled={missing.length > 0} onClick={() => void save()}>
              {t("common.save")}
            </button>
            {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
          </div>
        )}
      </div>
    </>
  );
}
